import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, History, Pause, Play, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDeleteSchedule, useSchedules, useUpdateSchedule } from '../hooks/schedules';
import { relativeTime } from '../search/controls';
import { useDiscoveryPaths } from '../workspace/paths';

/**
 * Searches that re-run themselves into this folder.
 *
 * The premise, worth stating on screen because it isn't obvious: a search
 * exhausts long before its niche does. TikTok stops paginating, not posting,
 * so the same query tomorrow reaches creators it couldn't today. Repeating is
 * safe because the registry drops anyone already known before an enrich call —
 * a repeat run adds only new people.
 *
 * `lastAdded` is the number that matters here. A schedule quietly returning
 * zero for a fortnight is spending API budget for nothing, and that should be
 * visible without opening a job log.
 */
export function SchedulePanel({ folderPublicId }: { folderPublicId: string }): JSX.Element | null {
  const { data: schedules } = useSchedules(folderPublicId);
  const update = useUpdateSchedule(folderPublicId);
  const remove = useDeleteSchedule(folderPublicId);
  const paths = useDiscoveryPaths();

  if (!Array.isArray(schedules) || schedules.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <CalendarClock className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="text-sm font-semibold">Scheduled searches</h3>
        {/* What each run added lives on the Repeat runs page, filtered to this folder. */}
        <Link
          to={paths.repeatRuns({ folder: folderPublicId })}
          className="ml-auto text-sm font-medium text-brand underline-offset-2 hover:underline"
        >
          View run history →
        </Link>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        A search runs out of pages before it runs out of creators. These re-run on a cadence and only ever add people
        the team hasn't already seen.
      </p>
      <ul className="space-y-1">
        {schedules.map((s) => (
          <li
            key={s.publicId}
            className={cn('flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-2', !s.enabled && 'opacity-70')}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{s.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                Every {s.everyDays}d · {s.enabled ? `next ${relativeTime(s.nextRunAt)}` : s.pausedReason === 'dry' ? 'paused automatically: its runs kept adding nobody' : 'paused'}
                {/* Zero is stated rather than hidden: a schedule that stopped
                    earning is the thing you want to notice. */}
                {s.runCount > 0 && ` · last run added ${s.lastAdded ?? 0}, ${s.totalAdded} total`}
              </p>
            </div>
            <Button asChild variant="ghost" size="icon" className="h-8 w-8">
              <Link
                to={paths.repeatRuns({ folder: folderPublicId, schedule: s.publicId })}
                aria-label={`Run history for ${s.name}`}
                title="Run history"
              >
                <History className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => update.mutate({ publicId: s.publicId, patch: { enabled: !s.enabled } })}
              aria-label={s.enabled ? `Pause ${s.name}` : `Resume ${s.name}`}
              title={s.enabled ? 'Pause' : 'Resume'}
            >
              {s.enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <ConfirmDialog
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:text-destructive-strong"
                  aria-label={`Delete ${s.name}`}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              }
              title="Delete this scheduled search?"
              description={`"${s.name}" stops re-running. Creators it already added stay in the folder.`}
              tone="destructive"
              confirmLabel="Delete schedule"
              pending={remove.isPending}
              onConfirm={() => remove.mutate(s.publicId)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The "repeat this" control that sits inside the search form. */
export function ScheduleToggle({
  enabled,
  everyDays,
  onEnabledChange,
  onDaysChange,
  updates,
}: {
  enabled: boolean;
  everyDays: string;
  onEnabledChange: (v: boolean) => void;
  onDaysChange: (v: string) => void;
  /** Name of a schedule in this folder that this search would update rather than duplicate. */
  updates?: string | null;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label className="inline-flex cursor-pointer items-center gap-2.5">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(ev) => onEnabledChange(ev.target.checked)}
          className="h-4 w-4 rounded border-input accent-[var(--brand)]"
        />
        <span className={enabled ? 'font-medium text-foreground' : 'text-foreground/85'}>Repeat this search</span>
      </label>
      {enabled && (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          every
          <Input
            type="number"
            min={1}
            max={90}
            aria-label="Repeat every (days)"
            value={everyDays}
            onChange={(ev) => onDaysChange(ev.target.value)}
            className="h-8 w-16"
          />
          days, starting after this search starts
        </span>
      )}
      {enabled && updates && (
        <p className="basis-full text-xs text-muted-foreground" data-schedule-updates>
          "{updates}" already repeats in this folder. It will be updated to these settings instead of adding a second
          schedule.
        </p>
      )}
    </div>
  );
}
